import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Lottie from "react-lottie";
import SuccessPopup from "components/common/SuccessPopup";
import FarmBusinessPopup from "./FarmBusinessPopup";
import RegistrationMap from "./RegistrationMap";
import BackDropLoader from "components/common/BackDropLoader";
import {
  SITE_REGISTRATION_ERROR,
  REGISTERED_SITE,
  PLEASE_SELECT_SITE_LOCATION_ON_MAP,
  PLEASE_ENTER_SITE_NAME,
  PLEASE_SELECT_FARM,
  PLEASE_ENTER_TOTAL_ACRES,
  PLEASE_ENTER_FALLOW_ACRES,
  FALLOW_ACRES_CANNOT_EXCEED_TOTAL_ACRES,
  SELECT_SITE_LOCATION_ON_MAP,
  SITE_NAME_ALREADY_EXISTS,
  BUSINESS_AND_FARMS_FETCH_ERROR,
  LABEL_ENTER_SITE_NAME,
  LABEL_ENTER_TOTAL_ACRES,
  LABEL_ENTER_FALLOW_ACRES,
  LABEL_SELECT_FARM,
  TOTAL_ACRES_MUST_BE_GREATER_THAN_ZERO,
  ADD_NEW_SITE,
  NEW_FARM,
} from "configurations/MessageConstants";
import { SITE_REGISTRATION_LOTTIE_OPTIONS } from "configurations/Constants";
import SiteService from "services/SiteService";
import "styles/SiteRegistration.css";
import UserService from "services/UserService";
import {
  setBusinesses,
  setFarmsBusinesses,
} from "reduxStore/slices/globalSlice";
import { InputHelper } from "utils/InputHelper";

const SiteRegistration = () => {
  const dispatch = useDispatch();
  const [geometry, setGeometry] = useState(null);
  const [showFarmModal, setShowFarmModal] = useState(false);
  const [registeredSite, setRegisteredSite] = useState(null);
  const [registeredSiteSuccess, setRegisteredSiteSuccess] = useState(false);

  const businessList = useSelector((state) => state.global.businesses);
  const farmBusinessList = useSelector((state) => state.global.farmsBusinesses);
  const sites = useSelector((state) => state.site.sites);

  const defaultValues = {
    name: "",
    farmName: "",
    businessName: "",
    totalArea: "",
    fallowArea: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({ defaultValues });

  const totalArea = watch("totalArea");

  useEffect(() => {
    getFarmsBusinesses();
  }, [dispatch]);

  const getFarmsBusinesses = async () => {
    try {
      const entities = await UserService.getFarmsBusinesses();
      dispatch(setBusinesses(entities.businesses));
      dispatch(setFarmsBusinesses(entities.farmsBusinesses));
    } catch (error) {
      console.error(BUSINESS_AND_FARMS_FETCH_ERROR, error);
    }
  };

  const handleClear = () => {
    reset(defaultValues);
    setGeometry(null);
    clearErrors();
    setRegisteredSite(null);
    setRegisteredSiteSuccess(false);
  };

  const onSubmit = async (data) => {
    if (!geometry) {
      setError("geometry", {
        type: "required",
        message: PLEASE_SELECT_SITE_LOCATION_ON_MAP,
      });
      return;
    }
    clearErrors("geometry");
    try {
      const payload = {
        ...data,
        geometry: {
          latitude: geometry.lat,
          longitude: geometry.lng,
        },
      };
      await SiteService.registerSite(payload);
      setRegisteredSite(payload);
      setRegisteredSiteSuccess(true);
    } catch (error) {
      toast.error(error.response.data.detail);
      console.error(SITE_REGISTRATION_ERROR, error);
    }
  };

  const handleMapClick = (coordinates) => {
    setGeometry(coordinates);
    clearErrors("geometry");
  };

  const handleNewFarm = () => {
    setShowFarmModal(true);
  };

  const handleCloseFarmBusinessPopup = () => {
    setShowFarmModal(false);
  };

  const handleAddFarmBusiness = (farmData) => {
    setValue("farmName", farmData.farm);
    setValue("businessName", farmData.business);
    const newFarmBusiness = {
      farmName: farmData.farm,
      businessName: farmData.business,
    };
    if (!farmBusinessList.some((farm) => farm.farmName === farmData.farm)) {
      dispatch(setFarmsBusinesses([...farmBusinessList, newFarmBusiness]));
    }
    if (!businessList.some((business) => business === farmData.business)) {
      dispatch(setBusinesses([...businessList, farmData.business]));
    }
    setShowFarmModal(false);
  };

  return (
    <div className="scrollable-container">
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={SITE_REGISTRATION_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      <h5 className="mb-3">{ADD_NEW_SITE}</h5>
      <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Row className="g-4 mb-3">
          <Col xs={12} md={6} lg={6} xl={3}>
            <Form.Group controlId="formSiteName">
              <Form.Label>Site Name</Form.Label>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: PLEASE_ENTER_SITE_NAME,
                  validate: (value) => {
                    const siteExists = sites.some(
                      (site) => site.name.toLowerCase() === value.toLowerCase()
                    );
                    return !siteExists || SITE_NAME_ALREADY_EXISTS;
                  },
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    className="input-underline"
                    type="text"
                    placeholder={LABEL_ENTER_SITE_NAME}
                    isInvalid={!!errors.name}
                    maxLength={50}
                  />
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={6} xl={3}>
            <Form.Group controlId="formFarm">
              <Form.Label className="d-flex justify-content-between">
                <span>Farm</span>
                <Button
                  variant="link"
                  className="new-farm-button"
                  onClick={handleNewFarm}
                  type="button"
                >
                  <i className="fa-solid fa-circle-plus me-1" />
                  <small>{NEW_FARM}</small>
                </Button>
              </Form.Label>
              <Controller
                name="farmName"
                control={control}
                rules={{ required: PLEASE_SELECT_FARM }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    className="input-underline"
                    aria-label="Select Farm"
                    isInvalid={!!errors.farmName}
                    onChange={(e) => {
                      field.onChange(e);
                      const selectedFarm = farmBusinessList.find(
                        (farm) => farm.farmName === e.target.value
                      );
                      setValue("businessName", selectedFarm?.businessName);
                    }}
                  >
                    <option value="">{LABEL_SELECT_FARM}</option>
                    {[
                      ...new Set(farmBusinessList.map((farm) => farm.farmName)),
                    ].map((farmName) => (
                      <option key={farmName} value={farmName}>
                        {farmName}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.farmName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={6} md={6} lg={6} xl={3}>
            <Form.Group controlId="formTotalAcres">
              <Form.Label>Total Acres</Form.Label>
              <Controller
                name="totalArea"
                control={control}
                rules={{
                  required: PLEASE_ENTER_TOTAL_ACRES,
                  validate: (value) =>
                    Number(value) > 0 || TOTAL_ACRES_MUST_BE_GREATER_THAN_ZERO,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    className="input-underline"
                    placeholder={LABEL_ENTER_TOTAL_ACRES}
                    isInvalid={!!errors.totalArea}
                    maxLength={10}
                    onChange={(e) => {
                      InputHelper.validateFloatNumberInput(e, field.onChange);
                    }}
                  />
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.totalArea?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={6} md={6} lg={6} xl={3}>
            <Form.Group controlId="formFallowAcres">
              <Form.Label>Fallow Acres</Form.Label>
              <Controller
                name="fallowArea"
                control={control}
                rules={{
                  required: PLEASE_ENTER_FALLOW_ACRES,
                  validate: (value) =>
                    Number(value) <= Number(totalArea) ||
                    FALLOW_ACRES_CANNOT_EXCEED_TOTAL_ACRES,
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    className="input-underline"
                    type="text"
                    placeholder={LABEL_ENTER_FALLOW_ACRES}
                    isInvalid={!!errors.fallowArea}
                    maxLength={10}
                    onChange={(e) => {
                      InputHelper.validateFloatNumberInput(e, field.onChange);
                    }}
                  />
                )}
              />
              <Form.Control.Feedback type="invalid">
                {errors.fallowArea?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            {errors.geometry ? (
              <div className="validation-error">{errors.geometry.message}</div>
            ) : (
              <small>{SELECT_SITE_LOCATION_ON_MAP}</small>
            )}
          </div>
          <RegistrationMap
            geometry={geometry}
            handleMapClick={handleMapClick}
          />
        </div>
        <div className="d-flex justify-content-end gap-2">
          <Button variant="outline-primary" onClick={handleClear} type="button">
            Clear
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            Register
          </Button>
        </div>
      </Form>
      <FarmBusinessPopup
        showFarmModal={showFarmModal}
        businessList={businessList}
        farmBusinessList={farmBusinessList}
        onAdd={handleAddFarmBusiness}
        onClose={handleCloseFarmBusinessPopup}
      />
      {registeredSiteSuccess && (
        <SuccessPopup
          message={REGISTERED_SITE}
          value={registeredSite.name}
          show={registeredSiteSuccess}
          handleClose={handleClear}
        />
      )}
    </div>
  );
};

export default SiteRegistration;
